import React, {FunctionComponent, useCallback, useLayoutEffect, useMemo, useState} from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    MenuItem,
    TextField,
    Typography,
    Unstable_Grid2 as Grid
} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import ReduxStoreRoutes from "../../../core/models/static/routes/redux-store-routes";
import useIsMounted from "../../hooks/use-is-mounted";
import {setReduxDialogState} from "../../redux/slices/dialogs/actions";
import Button from "../../components/base/button/button";


const RemoveConfirmationDialog: FunctionComponent = () => {
    const dispatch = useDispatch();
    const state = useSelector(state => state?.[ReduxStoreRoutes.dialogs.base][ReduxStoreRoutes.dialogs.removeConfirmation] ?? {});
    const [removing, setRemoving] = useState(false);
    const [replacement, setReplacement] = useState<any | null>(null);
    const isMounted = useIsMounted();

    const {hideCloseButton, disableBackdropClick, mandatory, removedEntryTitle, forceReplace, ...dialogProps} = state;

    const needsToReplace = useMemo(() => !!state?.data?.length, [state?.data?.length]);

    /**
     * As soon as the dialog opens, resets the state values.
     */
    useLayoutEffect(() => {
        if (state?.open) {
            setRemoving(false);
            setReplacement(null);
        }
    }, [state?.open])

    /**
     * Closes the dialog by dispatching its relevant action to the redux store.
     */
    const closeDialog = useCallback(async (successful = false, data = null) => {
        if (state?.onClose) {
            setRemoving(true);
            const resultFlag = await state.onClose(successful, data);
            if (!isMounted())
                return;
            setRemoving(false);
            if (successful && !resultFlag)
                return;
        }
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {open: false}));
    }, [state, dispatch, isMounted])

    /**
     * Closes the dialog if the values from the state do not prevent the closing of the dialog.
     */
    const onDialogClosed = useCallback((event: Object, reason: 'backdropClick' | 'escapeKeyDown' | 'closeClick') => {
        switch (reason) {
            case "backdropClick":
                if (!state.disableBackdropClick && !removing)
                    closeDialog().then();
                break;
            case "closeClick":
                if (!state.hideCloseButton && !removing)
                    closeDialog().then();
                break;
            case "escapeKeyDown":
                if (!state.disableEscapeKeyDown && !removing)
                    closeDialog().then();
                break;
            default:
                break;
        }
    }, [removing, closeDialog, state.disableBackdropClick, state.disableEscapeKeyDown, state.hideCloseButton])


    return (
        <>
            <Dialog
                {...dialogProps}
                disableEscapeKeyDown={!mandatory}
                onClose={onDialogClosed}
            >
                <DialogTitle fontWeight={600}>
                    Remove Confirmation
                </DialogTitle>
                <DialogContent dividers className={'py-3 pb-5'}>
                    {
                        !needsToReplace &&
                        <DialogContentText>
                            {
                                !!state.title &&
                                state.title
                            }
                            {
                                !state.title &&
                                <>
                                    Are you sure you want to remove {state?.removedEntryTitle ?? "the selected record"}?
                                </>
                            }
                        </DialogContentText>
                    }
                    {
                        needsToReplace &&
                        <>
                            <DialogContentText variant={'body1'}>
                                {
                                    !!state.title &&
                                    state.title
                                }
                                {
                                    !state.title &&
                                    <>
                                        Are you sure you want to remove {state?.removedEntryTitle ?? "the selected record"}?
                                    </>
                                }
                                <br/>
                                Please select the replacement record before deleting the requested record:
                            </DialogContentText>
                            <Grid container xs={12} gap={2} spacing={2} marginInline={0} marginBlock={2}>
                                <Grid xs={'auto'} padding={0}>
                                    <Typography variant={'body1'}>
                                        To be removed:
                                    </Typography>
                                </Grid>
                                <Grid xs padding={0}>
                                    <Typography variant={'body1'}>
                                        {state?.removedEntryTitle ?? "the selected record"}
                                    </Typography>
                                </Grid>
                                <Grid xs={12} padding={0}/>
                                <Grid xs={'auto'} padding={0} sx={{display: 'flex', alignItems: 'center'}}>
                                    <Typography variant={'body1'}>
                                        To be replaced with:
                                    </Typography>
                                </Grid>
                                <Grid xs padding={0}>
                                    <TextField
                                        select
                                        fullWidth
                                        label={"Replacement"}
                                        value={replacement?.id ?? ""}
                                        onChange={(e) => setReplacement(state?.data?.find(a => a.id === e.target.value) ?? null)}
                                        SelectProps={{
                                            renderValue: (value: any) => state?.data?.find(e => e.id === value)?.title ?? "",
                                        }}
                                    >
                                        {
                                            state?.data?.map(e => (
                                                <MenuItem key={e.id} value={e.id}>
                                                    {e.title}
                                                </MenuItem>
                                            ))
                                        }
                                    </TextField>
                                </Grid>
                            </Grid>
                        </>
                    }
                </DialogContent>
                <DialogActions sx={{display: 'flex', justifyContent: 'space-between', padding: 0}}>
                    <Button
                        variant={'dialog'}
                        type={'button'}
                        color={'primary'}
                        disabled={removing}
                        onClick={() => onDialogClosed({}, 'closeClick')}
                        // @ts-ignore
                        borderRadius={'0 8px 0 8px'}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        disabled={(needsToReplace && (!replacement && forceReplace !== false)) || removing}
                        onClick={() => closeDialog(true, replacement)}
                        // @ts-ignore
                        borderRadius={'8px 0 8px 0'}
                        autoFocus
                    >
                        {
                            removing
                                ? "Removing..."
                                : "Remove".concat(needsToReplace && !!replacement ? " And Replace" : "")
                        }
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default RemoveConfirmationDialog;
